import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { MAIN_PAGE_TITLE } from '@/constants/defaults';

import { Breadcrumb } from '../types/breadcrumb';
import { PageContainerStateContext } from './PageContainerContext';
import { PageContainerContext } from './PageContainerContext.types';

const EMPTY_BREADCRUMBS: Breadcrumb[] = [];

export type PageContainerProviderProps = {
    defaultFullpage?: boolean;
};

export function PageContainerProvider({
    children,
    defaultFullpage,
}: PageContainerProviderProps & { children: ReactNode }) {
    const [pageTitle, setPageTitle] = useState<string>('');
    const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Breadcrumb[]>(EMPTY_BREADCRUMBS);
    const [isFullPage, setIsFullPage] = useState<boolean>(defaultFullpage ?? true);

    const setAsPrivatePage = useCallback(
        (isFullpage = false) => {
            defaultFullpage === undefined && setIsFullPage(isFullpage);
        },
        [defaultFullpage],
    );

    const setAsPublicPage = useCallback(
        (isFullpage = true) => {
            defaultFullpage === undefined && setIsFullPage(isFullpage);
        },
        [defaultFullpage],
    );

    const handleSetPageTitle = useCallback((pageTitle?: string) => {
        document.title = `${pageTitle ? `${pageTitle} - ` : ''} ${MAIN_PAGE_TITLE}`;
        setPageTitle(pageTitle ?? '');
    }, []);

    const handleSetPageBreadcrumbs = useCallback((breadcrumbs?: Breadcrumb[]) => {
        setPageBreadcrumbs(breadcrumbs ?? EMPTY_BREADCRUMBS);
    }, []);

    const value: PageContainerContext = useMemo(
        () => ({
            pageTitle,
            setPageTitle: handleSetPageTitle,
            pageBreadcrumbs,
            setPageBreadcrumbs: handleSetPageBreadcrumbs,
            isFullPage,
            setAsPrivatePage,
            setAsPublicPage,
        }),
        [
            pageTitle,
            handleSetPageTitle,
            pageBreadcrumbs,
            handleSetPageBreadcrumbs,
            isFullPage,
            setAsPrivatePage,
            setAsPublicPage,
        ],
    );

    return <PageContainerStateContext value={value}>{children}</PageContainerStateContext>;
}
